import { NatalkaSprite, FLIP_LAYERS } from './NatalkaSprite.js';
import { ANIM_ENABLED } from './walkAnim.js';
import { ChatBubble } from './ChatBubble.js';
import { defaultTweak } from './clothDefaults.js';
import { BIKE } from '../config.js';
import {
  bikeDisplayHeight, bikeWheelOffset, CHARACTER_ART_H, carDisplayHeightAt,
  carGroundYFromCentre, groundYFromCentre, perspectiveScale,
} from './carCatalog.mjs';
import { BikeSprite } from './BikeSprite.js';
import { wornBikeItems } from './bikeArtKeys.mjs';
import { speedFromPath } from './rideMotion.mjs';
import { createPacketClock } from './packetClock.mjs';
import { isSchoolCarId } from './schoolCar.mjs';
import { LABEL_DEPTH } from './ySort.js';
import { iconText } from './iconText.js';
import { headTop } from './poseLayers.mjs';
import { preloader } from '../scenes/PreloadScene.js';

// Intentional render delay → smoothness. It has to outlast the gap between two moves (100 ms, stretched to
// ~117 by the sender's frames) plus the network's jitter, or the peer stands still at their last move until the
// next one lands and then jumps (owner 2026-11-02: a ride in someone's car was "quite laggy"; was 100).
const BUFFER_MS = 200;
// A car is much wider than an avatar and its roof is lower than a head — the name tag sits
// just above the ROOF (cars differ in height now), not in the sky above the bonnet.
const CAR_LABEL_ABOVE_ROOF = 35;
// Offsets for a walking / seated peer, in un-scaled world units: multiplied by the scene's
// worldScale, since outdoors the avatar under the tag is drawn smaller.
const WALK_LABEL_OFFSET = 150;
const SIT_LABEL_OFFSET = 100;
// On a sofa / in a bed the tag is this far above the head (standing: 150 above the centre = 30 above the head).
const REST_LABEL_ABOVE_HEAD = 30;
// A rider's tag clears the head: the layered rider's box is head-to-ground, so half its height plus this (px).
const BIKE_LABEL_ABOVE_HEAD = 16;

// Build a tweak source from a peer's cloth_tweaks (keyed by item id, no "cloth_" prefix),
// so each remote player renders with their OWN garment scale/offset — not the viewer's.
// Falls back to the per-garment defaults for the PEER's character, like the local player.
function tweakSourceFor(outfit, character = 'natalka') {
  const peerTweaks = outfit?.cloth_tweaks || {};
  return { get: (clothKey) => ({ ...defaultTweak(clothKey, character), ...(peerTweaks[clothKey.replace(/^cloth_/, '')] || {}) }) };
}

// A remote peer rendered with the same outfit pipeline as Natalka, plus a name label
// and a ~200ms interpolation buffer so sparse server updates glide. Swaps to the
// layered rider (a BikeSprite in their outfit and bike design) when the peer is riding (mode === 'bike').
export class RemoteAvatar {
  // opts.perchAnchor(perch) -> {x, y, pose, flip} | null: where a perch ("<placement id>/<seat>") is in this scene —
  // remotePresence binds it to the room's sofas and beds. Without it a resting peer is drawn standing at x/y.
  constructor(scene, peer, opts = {}) {
    this.scene = scene;
    this.id = peer.id;
    this.name = peer.name;
    // The scene's real-world scale: 1 indoors, OUTDOOR_CHARACTER_SCALE on the street and
    // the training route, where a peer is a 1.2 m child beside a 1.54 m car.
    this._worldScale = scene.worldScale ?? 1;
    this._perspective = scene.perspective;   // undefined = the street's default profile
    this.sprite = new NatalkaSprite(scene, peer.x, peer.y, {
      tweakSource: tweakSourceFor(peer.outfit, peer.character ?? 'natalka'), noTweakHud: true, trackIdle: false,
      character: peer.character ?? 'natalka', worldScale: this._worldScale,
      walkAnim: ANIM_ENABLED,   // peers walk with the same sheets as the local player
      look: peer.outfit?.look ?? null,   // their skin / eyes / hair (look/)
    });
    if (Number.isInteger(peer.dir)) this.sprite.setFacing8?.(peer.dir);
    this._dir8 = Number.isInteger(peer.dir) ? peer.dir : null;
    this._prevPos = null;
    this.sprite.setDepth(9);
    this.sprite.setOutfit(peer.outfit?.equipped);
    this.sprite.setSleeping(!!peer.idle);   // peer may already be asleep when we arrive

    this._character = peer.character ?? 'natalka';
    this._bikeType = peer.bike_type ?? 'pink';
    this._bikeDesign = peer.bike_design ?? null;
    this._equipped = peer.outfit?.equipped ?? null;
    this._look = peer.outfit?.look ?? null;
    this._rideHeld = null;   // speedFromPath's memory across the still frames between packets
    // Set before anything sizes a body: `groundY` reads it to know which part of the peer
    // (feet / bike wheels / car wheels) is the one standing on the road.
    this._mode = peer.mode || 'walk';   // peer may already be riding when we arrive
    this._carModel = peer.car_model ?? null;
    this._carAspect = 2;
    // The layered rider, built the first time this peer rides; it draws once its atlases have loaded.
    this.bike = null;

    this.label = iconText(scene, peer.x, peer.y - WALK_LABEL_OFFSET * this._worldScale, peer.name, {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 6, y: 2 },
    }).setOrigin(0.5).setDepth(LABEL_DEPTH);
    this._clock = createPacketClock();   // places each move by when it was sent, not when it arrived
    this._buf = [{ t: this._clock(performance.now(), undefined), x: peer.x, y: peer.y }];
    this._facing = 1;
    this._seat = Number.isInteger(peer.seat) ? peer.seat : null;
    this._perchAnchor = opts.perchAnchor ?? null;
    this._perch = this._mode === 'rest' ? (peer.perch ?? null) : null;
    this._hidden = false;               // forced-hidden (e.g. host shown seated in furniture)
    this._bubble = null;
    // Car mode — the image is created lazily: most peers never drive, and a scene full of
    // unused car sprites is a scene full of wasted texture binds.
    this.car = null;
    this.carId = peer.car_id ?? null;
    // The driver's own km/h, as it came off the wire. A car is only boardable while it is
    // standing still (the server answers CAR_MOVING otherwise), so the street has to know.
    this.speed = peer.speed ?? 0;
    this._passengers = 0;
    this._passengerText = null;
    this._applyLabel();   // a peer can already be mid-lesson when we walk in on them
    this._applyMode();
  }

  // Force this avatar invisible regardless of mode — a Game Hall player at a table, a passenger in someone's car.
  setHidden(v) { this._hidden = v; this._applyMode(); }

  // What the name tag says. Somebody in the driving school's Yaris (`car_id` „school:…")
  // is having a lesson — say so, so the street can see why that car drives like that.
  _labelText() {
    return isSchoolCarId(this.carId) ? `${this.name} · Nauka jazdy` : this.name;
  }

  _applyLabel() { this.label?.setText(this._labelText()); }

  get mode() { return this._mode; }
  get seat() { return this._seat; }
  /** The sofa / bed spot a resting peer is on ("<placement id>/<seat>"), else null. */
  get perch() { return this._perch; }
  get facing() { return this._facing === -1 ? 'left' : 'right'; }
  /** Where this peer is on screen right now (a driver: where their car is). */
  get x() { return this.sprite.x; }
  get y() { return this.sprite.y; }
  get passengers() { return this._passengers; }
  /** Which character this peer plays ('natalka' | 'ken'). */
  get character() { return this._character; }
  get look() { return this._look; }

  // The rider, at the size the sender's BikeController draws it: BIKE.RIDER_H indoors, a child-on-a-bike outdoors
  // times the y-perspective of the line its wheels are on.
  _sizeBike() {
    if (!this.bike) return;
    const h = this._outdoor ? this._bikeBase() * perspectiveScale(this.groundY, this._perspective) : this._bikeBase();
    if (Math.abs(h - this.bike.displayHeight) > 0.01) this.bike.setDisplayHeight(h);
  }

  _rig() { return this.scene.cache.json.get('bike_rig'); }
  _riderH() { return this._outdoor ? undefined : BIKE.RIDER_H; }

  /** This scene's un-foreshortened rider height for the peer's bike type. */
  _bikeBase() { return bikeDisplayHeight('rider', this._bikeType, this._rig(), this._riderH()); }

  /** …and how far below the rider's centre its wheels are, on the reference plane. */
  _bikeWheelBase() { return bikeWheelOffset('rider', this._bikeType, this._rig(), this._riderH()); }

  /** The clothes the peer has on that have ride layers. */
  _bikeItems() {
    const rig = this._rig();
    return rig ? wornBikeItems(rig, this._character, this._equipped) : [];
  }

  _ensureBike() {
    if (this.bike) return this.bike;
    this.bike = new BikeSprite(this.scene, this.sprite.x, this.sprite.y, {
      type: this._bikeType, design: this._bikeDesign, char: this._character, items: this._bikeItems(),
      dir8: this._dir8 ?? 0, visible: false,
    }).setDepth(9);
    this.bike.setLook(this._look);
    this._sizeBike();
    return this.bike;
  }

  get _outdoor() { return this._worldScale !== 1; }
  /** Drawn in a y-perspective: outdoors, or in a room that sets `scene.perspective`. */
  get _scaled() { return this._worldScale !== 1 || !!this._perspective; }

  /**
   * Where this peer touches the ground: her feet, her bike's wheels or her car's, worked
   * back out of the sprite-CENTRE y that came off the wire (the sender scaled by exactly the
   * same rule, so the inverse is exact). Undefined indoors, where there is no perspective
   * and the depth sort uses a flat half-height.
   */
  get groundY() {
    if (!this._scaled) return undefined;
    const y = this.sprite.y;
    if (this._mode === 'car' && this._carModel && this._outdoor) return carGroundYFromCentre(this._carModel, y);
    if (this._mode === 'bike' && this._outdoor) return groundYFromCentre(this._bikeWheelBase(), y, this._perspective);
    return groundYFromCentre(CHARACTER_ART_H * this._worldScale / 2, y, this._perspective);
  }

  _carTexture() {
    const key = `car_${this._carModel}_side`;
    if (this.scene.textures.exists(key)) return key;
    const model = this._carModel;
    this._swapInLater(key, () => { if (this._carModel === model && this.car?.active) this._ensureCar(); });
    return 'car_panda_side';
  }

  // Not loaded yet (a phone does not prefetch every model): draw the stand-in now and swap the
  // real picture in when it lands. One request per key — these run on every move.
  _swapInLater(key, apply) {
    this._awaiting ??= new Set();
    if (this._awaiting.has(key)) return;
    this._awaiting.add(key);
    preloader.whenReady(key, () => { this._awaiting.delete(key); apply(); });
  }

  // Build (or re-texture) the car body. Called the first time this peer shows up in a car —
  // from the roster, from a join, or from the move that started their drive.
  _ensureCar() {
    if (!this._carModel) return null;
    const key = this._carTexture();
    if (!this.car) {
      this.car = this.scene.add.image(this.sprite.x, this.sprite.y, key).setDepth(9);
    } else if (this.car.texture?.key !== key) {
      this.car.setTexture(key);
    } else {
      return this.car;
    }
    const src = this.scene.textures.get(key).getSourceImage();
    this._carAspect = src.width / src.height;
    this._sizeCar();
    return this.car;
  }

  // A driving peer's car is drawn at the perspective size of the lane it is in — its own y,
  // not ours: the far lane is visibly smaller than the near one.
  _sizeCar() {
    if (!this.car || !this._carModel) return;
    const h = this._outdoor
      ? carDisplayHeightAt(this._carModel, this.groundY)
      : carDisplayHeightAt(this._carModel);
    this.car.setDisplaySize(h * (this._carAspect || 2), h);
  }

  // Peer went AFK / came back — show or hide their Zzz indicator.
  setSleeping(v) { this.sprite.setSleeping(!!v); }

  pushMove({ x, y, facing, mode, bike_type, bike_design, seat, perch, car_model, car_id, speed, dir, ts }) {
    this._buf.push({ t: this._clock(performance.now(), ts), x, y });
    if (Number.isInteger(dir)) this._dir8 = dir;
    if (this._buf.length > 10) this._buf.shift();
    if (facing) this._facing = facing === 'left' ? -1 : 1;
    const mounting = mode === 'bike' && this._mode !== 'bike';
    if (mode) this._mode = mode;
    this._seat = this._mode === 'sit' && Number.isInteger(seat) ? seat : null;
    this._perch = this._mode === 'rest' ? (perch ?? null) : null;
    // The server nulls these out for anyone it can't vouch for as the car's driver, so
    // trust them exactly as they arrive — including the null that ends a drive.
    if (car_model !== undefined) this._carModel = car_model;
    if (car_id !== undefined) { this.carId = car_id; this._applyLabel(); }
    if (speed !== undefined) this.speed = speed ?? 0;
    // Only a bike move names the bike; another type without a design means that type's default.
    const type = bike_type || this._bikeType;
    const design = bike_design !== undefined ? bike_design : (type !== this._bikeType ? null : this._bikeDesign);
    if (type !== this._bikeType || design !== this._bikeDesign) {
      this._bikeType = type;
      this._bikeDesign = design;
      this.bike?.setDesign(type, design);
      this._sizeBike();
    }
    this._applyMode();
    // Getting on: the stop pose, facing the way they set off.
    if (mounting && this.bike) {
      this.bike.setPosition(x, y).setFacing8(this._dir8 ?? this.bike.facing8).setStop();
      this._rideHeld = null;
    }
    this._applyFlip();
  }

  // Live wardrobe change — re-render with the peer's new equipped + tweaks.
  applyOutfit(outfit) {
    this.sprite._tweakSource = tweakSourceFor(outfit, this._character);
    this.sprite.setOutfit(outfit?.equipped);
    this.sprite.setLook(outfit?.look ?? null);
    this._equipped = outfit?.equipped ?? null;
    this._look = outfit?.look ?? null;
    this.bike?.setRider(this._character, this._bikeItems());
    this.bike?.setLook(this._look);
    this._applyFlip();
  }

  /** Where a resting peer is drawn — null when not resting, or when the piece is not in this room (yet). */
  _restAnchor() {
    return this._mode === 'rest' && this._perch ? (this._perchAnchor?.(this._perch) ?? null) : null;
  }

  // On a sofa / in a bed: the pose at the perch's spot; anything else (or a perch we cannot place) stands.
  _applyPose(anchor = this._restAnchor()) {
    if (anchor) this.sprite.setPose(anchor.pose, { flip: anchor.flip });
    else this.sprite.setPose(null);
    return anchor;
  }

  _applyMode() {
    this.sprite.setSeated(this._mode === 'sit');
    this._applyPose();
    const driving = this._mode === 'car';
    const riding = this._mode === 'bike';
    if (driving) this._ensureCar();
    if (riding) this._ensureBike();
    if (this._hidden) {
      this.sprite.setVisible(false);
      this.bike?.setVisible(false);
      this.car?.setVisible(false);
      this.label?.setVisible(false);
      this._passengerText?.setVisible(false);
      return;
    }
    this.label?.setVisible(true);
    this._passengerText?.setVisible(driving && this._passengers > 0);
    this.sprite.setVisible(!riding && !driving);
    this.bike?.setVisible(riding);
    this.car?.setVisible(driving && !!this._carModel);
  }

  _applyFlip() {
    // The car art faces LEFT, so it's the mirror of the character sprites: flip it when
    // the peer drives to the right.
    this.car?.setFlipX(this._facing === 1);
    for (const key of FLIP_LAYERS) {
      const sp = this.sprite.layers?.[key];
      if (sp) sp.setFlipX(this._facing === -1);
    }
  }

  // How many people ride along in this peer's car — fed by the server's `car_passengers`
  // broadcast (a passenger's own move is downgraded to a walk, so their avatar is hidden
  // and only this count tells you they're aboard).
  setPassengerCount(n) {
    this._passengers = Math.max(0, n | 0);
    if (this._passengers > 0 && !this._passengerText) {
      this._passengerText = iconText(this.scene, 0, 0, '', {
        fontFamily: 'system-ui', fontSize: '14px', color: '#fff',
        backgroundColor: '#000000aa', padding: { x: 5, y: 1 },
      }).setOrigin(0.5).setDepth(LABEL_DEPTH);
    }
    this._passengerText?.setText(`{friends} ${this._passengers}`);
    this._applyMode();
  }

  update() {
    const renderT = performance.now() - BUFFER_MS;
    const buf = this._buf;
    let a = buf[0], b = buf[buf.length - 1];
    for (let i = 0; i < buf.length - 1; i++) {
      if (buf[i].t <= renderT && buf[i + 1].t >= renderT) { a = buf[i]; b = buf[i + 1]; break; }
    }
    const span = Math.max(1, b.t - a.t);
    const k = Math.max(0, Math.min(1, (renderT - a.t) / span));
    // Resting: pinned to the perch's spot on the piece (looked up every frame — the piece can be moved, or drawn
    // only after this peer arrived).
    const rest = this._applyPose();
    const x = rest ? rest.x : a.x + (b.x - a.x) * k;
    const y = rest ? rest.y : a.y + (b.y - a.y) * k;
    this.sprite.setPosition(x, y);
    // Motion comes from the interpolated path; when still, the sent facing holds.
    const now = performance.now(); const p = this._prevPos;
    const vx = p ? (x - p.x) / Math.max(1, now - p.t) * 1000 : 0, vy = p ? (y - p.y) / Math.max(1, now - p.t) * 1000 : 0;
    this._prevPos = { x, y, t: now };
    const moving = Math.hypot(vx, vy) > 8;
    if (this._mode === 'bike' && this.bike) {
      // Riding: how fast it goes says how fast they pedal and the wheels turn; held across the gaps between
      // packets, so the foot only goes down after a real stop.
      const ride = p ? speedFromPath(p, { x, y }, now - p.t, this._rideHeld) : { speed: 0, pedalling: false, held: null };
      this._rideHeld = ride.held;
      this.bike.setRide(ride);
      if (moving) this.bike.setMotion(vx, vy);
      else if (Number.isInteger(this._dir8)) this.bike.setFacing8(this._dir8);
    } else if (rest) {
      // Posed: no walk to drive.
    } else if (this.sprite.setMotion) {
      // Animated walk.
      if (moving) this.sprite.setMotion(vx, vy);
      else { if (Number.isInteger(this._dir8)) this.sprite.setFacing8(this._dir8); this.sprite.setMotion(0, 0); }
    }
    this.bike?.setPosition(x, y);
    this.car?.setPosition(x, y);
    // Outdoors everything that stands on the ground is sized by how far down the screen it
    // stands (see carCatalog's PERSPECTIVE), so re-fit whichever body is on show. Indoors
    // `_worldScale` is 1 and nothing here changes from frame to frame.
    if (this._scaled) {
      if (this._mode === 'car' && this._outdoor) this._sizeCar();
      else if (this._mode === 'bike' && this._outdoor) this._sizeBike();
      else if (rest) this.sprite.setWorldScale(this._worldScale * (rest.scale ?? 1));   // on a piece: the piece's size
      else this.sprite.setWorldScale(this._worldScale * perspectiveScale(this.groundY, this._perspective));
    }
    // Seated peers are cropped + scaled down, so their head is much lower than a standing
    // avatar's — keep the label just above it. A driver's tag rides on the car's roof, a rider's above the head.
    const offset = this._mode === 'car'
      ? (this.car?.displayHeight ?? 0) / 2 + CAR_LABEL_ABOVE_ROOF
      : this._mode === 'bike' && this.bike
        ? this.bike.displayHeight / 2 + BIKE_LABEL_ABOVE_HEAD
        : rest
          ? (REST_LABEL_ABOVE_HEAD - headTop(rest.pose).y) * this.sprite.worldScale
          : (this._mode === 'sit' ? SIT_LABEL_OFFSET : WALK_LABEL_OFFSET) * this.sprite.worldScale;
    const labelX = rest ? x + headTop(rest.pose).x * (rest.flip ? -1 : 1) * this.sprite.worldScale : x;
    this.label.setPosition(labelX, y - offset);
    this._passengerText?.setPosition(x, y - offset + 26);
  }

  setChat(text) {
    this._bubble?.destroy();
    this._bubble = new ChatBubble(this.scene, this.sprite, text);
  }

  destroy() {
    this.sprite.destroy();
    this.bike?.destroy();
    this.car?.destroy();
    this.label.destroy();
    this._passengerText?.destroy();
    this._bubble?.destroy?.();
  }
}
